import { IS_TEST } from '@trustoo/constants/environment'
import type { User } from '@trustoo/types/entity/user'

const LIVE_BUCKET_URL = 'https://static.trustoo.nl/'
const TEST_BUCKET_URL = 'https://s3-eu-west-1.amazonaws.com/static-test.trustoo.nl/'
const NO_AVATAR_PATH = 'icon/no_avatar.jpg'
const ABSOLUTE_URL = /^https?:\/\//i
const LEADING_SLASHES = /^\/+/

/**
 * Base URL of the public S3 bucket for the current environment: the test bucket in test and
 * development builds, the live bucket otherwise. For a consumer that appends keys itself; use
 * {@link getS3Url} for a single file.
 */
export const ENV_BASED_PUBLIC_BUCKET_URL = IS_TEST ? TEST_BUCKET_URL : LIVE_BUCKET_URL

/** The user fields an avatar URL is built from. */
export type AvatarUser = Pick<User, 'id' | 'avatar_filename'>

/** The provider fields an avatar URL is built from. */
export interface AvatarProvider {
    /** Ready-made URL from the API, which already substitutes the placeholder avatar. */
    avatarUrlSmall?: string | null
    user?: AvatarUser | null
}

/**
 * Absolute URL of a file in the public S3 bucket, served from the `.nl` domain in every country.
 *
 * Test and development builds read from the test bucket unless `useLive` is set. An absolute URL is
 * returned unchanged, so a value that may already be resolved can be passed through safely. A
 * missing or empty path returns an empty string rather than a bare bucket URL, and leading slashes
 * are dropped because S3 treats `//key` as a different, missing key.
 */
export function getS3Url(path: string | null | undefined, useLive = false): string {
    if (!path) {
        return ''
    }

    if (ABSOLUTE_URL.test(path)) {
        return path
    }

    const bucketUrl = useLive ? LIVE_BUCKET_URL : ENV_BASED_PUBLIC_BUCKET_URL

    return bucketUrl + path.replace(LEADING_SLASHES, '')
}

/**
 * Avatar URL of a provider or a user, or the placeholder avatar when they have none.
 *
 * A provider is recognised by its `avatarUrlSmall` or `user` field, and resolves through its user
 * when the API sent no ready-made URL.
 */
export function getAvatarUrl(owner: AvatarProvider | AvatarUser): string {
    if ('avatarUrlSmall' in owner && owner.avatarUrlSmall) {
        return owner.avatarUrlSmall
    }

    if ('user' in owner) {
        return owner.user ? getAvatarUrl(owner.user) : getS3Url(NO_AVATAR_PATH)
    }

    if ('avatar_filename' in owner && owner.avatar_filename) {
        return getS3Url(`users/${owner.id}/${owner.avatar_filename}`)
    }

    return getS3Url(NO_AVATAR_PATH)
}
